import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, catchError,  map, Observable, Subject, throwError, of, switchMap } from 'rxjs';
import { Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Company, User, UserProfile } from '../models/context.model';
import { ContextService } from '../services/context.service';
import { AppService } from '../../../app.service';
import { URLS_GUARD } from '../models/url-guard.enum';
import { UserType } from '../../../models/shared/enums/user-type.enum';

@Injectable({
	providedIn: 'root'
})
export class AuthService implements OnDestroy {
	private user = new BehaviorSubject<User>(null as any);

	readonly #appService = inject(AppService);
	readonly #router = inject(Router);
	readonly #contextService = inject(ContextService);
	readonly #httpClient = inject(HttpClient);
	//readonly #settingsService = inject(SettingsService);

	get getUserLogged(): Observable<User> {
		return this.user.asObservable();
	}

	setUserLogged(user: any): void {
		this.user.next(user);
	}

	get userLoggedValue(): User {
		return this.user.value;
	}

	private readonly _destroying$ = new Subject<void>();

	//token
	private token: BehaviorSubject<any> = new BehaviorSubject(null);

	setToken(_token: any) {
		this.token.next(_token);
	}

	get getTokenValue() {
		return this.token?.value ?? undefined;
	}

	get getTokenObservable() {
		return this.token.asObservable();
	}

	authentication(state: RouterStateSnapshot) {
		return this.getJwtToken().pipe(
			switchMap((initialRes: any) => {
				const tokenInfo = this.getDecodedAccessToken(initialRes.JwtToken);
				const user = this.userMapper(tokenInfo);

				if (user.UserType?.toLowerCase() === 'licensee' && (!user.Roles || user.Roles.length === 0)) {
					// Set the token temporarily for the syncUser call to be authenticated
					this.setToken(initialRes.JwtToken);
					// After syncing, get the new token and pass its response to the next operator
					// return this.settingsService.syncUser(Number(user.Id!)).pipe(switchMap(() => this.getJwtToken()));
				}
				// If no sync is needed, just pass on the original token response
				return of(initialRes);
			}),
			map((finalRes: any) => {
				// This is now the single point of truth for setting the token and context.
				// It uses the final token, whether it's the original or the refreshed one.
				this.setToken(finalRes.JwtToken);
				let tokenInfo = this.getDecodedAccessToken();
				this.mapContext(tokenInfo);

				this.setUserLogged(this.#contextService.context.Current.User);

				//recupero la rotta richiesta dalla sessione
				let route = state.url;
				if (route == URLS_GUARD.AUTH_URL) {
					route = localStorage.getItem('BARequestRoute') ?? state.url;
					localStorage.clear();
				}

				//navigazione riprenderà dalla rotta route
				return this.parseUrl(route);
			}),
			catchError((err: HttpErrorResponse) => {
				console.error(err);
				if (state.url != URLS_GUARD.AUTH_URL) {
					//metto in sessione l'ultima rotta visitata da recuperare dopo il login su /auth
					var currentPage = state.url;
					localStorage.setItem('BARequestRoute', currentPage);
					this.login();
					return throwError(() => false);
				}
				this.handleHttpError(err.status);
				return throwError(() => false);
			})
		);
	}

	isPhotographer() {
		return this.userLoggedValue?.UserType == UserType.PHOTOGRAPHER;
	}

	isCompany() {
		return this.userLoggedValue?.UserType == UserType.COMPANY;
	}

	getLicenseeCompanyId() {
		let profile = this.userLoggedValue.UserProfiles?.filter((p) => p.Id == this.userLoggedValue.CurrentProfileId);
		if (profile?.length) {
			return profile[0].Company?.Id;
		}
		return false;
	}

	handleHttpError(status: number) {
		switch (status) {
			case 401: {
				this.#router.navigateByUrl('error/unauthorized');
				break;
			}
			case 403:
				this.#router.navigateByUrl('error/forbidden');
				break;
			default:
				this.#router.navigateByUrl('error/server-error');
		}
	}

	ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

	login(userFlowRequest?: any) {
		location.href = this.#contextService.context.Settings.LoginUrl + '?returnUrl=' + this.#contextService?.context?.Settings?.AppPaths?.Local?.Base + '/auth';
	}

	logout() {
		this.#httpClient
			.post(this.#contextService.context.Settings.LogoffUrl + '?returnUrl=' + this.#contextService.context.Settings.LoginUrl, {}, { withCredentials: true })
			.subscribe(() => {
				this.setToken(undefined);
				this.setUserLogged(null);
				//con il logoff deve eliminarsi anche il redirect post login per poter tornare in home (dashboard)
				localStorage.clear();
				this.login();
			});
	}

	getDecodedAccessToken(token?: string): any {
		if (token) {
			try {
				return jwtDecode(token);
			} catch (Error) {
				return null;
			}
		}
		if (this.token.value) {
			try {
				return jwtDecode(this.token.value);
			} catch (Error) {
				return null;
			}
		}
	}

	userMapper(inputObject: any): User {
		const userObject: User = {
			Id: inputObject.Id,
			FirstName: inputObject.FirstName,
			LastName: inputObject.LastName,
			CurrentProfileId: inputObject.CurrentProfileId,
			ClientId: inputObject.ClientId,
			UserType: inputObject.UserType,
			ImgPath: inputObject.ImgPath,
			UserName: inputObject.UserName,
			UserProfiles: [],
			Roles: []
		};

		if(inputObject.Roles && inputObject.UserProfiles) return inputObject;

		// Iterate through indices and map UserProfiles
		let index = 0;
		while (inputObject[`UserProfiles[${index}].Id`] !== undefined) {
			const company: Company = {
				GroupId: inputObject[`UserProfiles[${index}].Company.GroupId`],
				Id: inputObject[`UserProfiles[${index}].CompanyId`]
			};
			const userProfile: UserProfile = {
				Id: inputObject[`UserProfiles[${index}].Id`],
				PlatformId: inputObject[`UserProfiles[${index}].PlatformId`],
				ProfileName: inputObject[`UserProfiles[${index}].ProfileName`],
				IsCustom: inputObject[`UserProfiles[${index}].IsCustom`] === 'True' ? true : false,
				Company: company
			};
			userObject.UserProfiles?.push(userProfile);
			index++;
		}

		index = 0;
		while (inputObject[`Roles[${index}]`] !== undefined) {
			userObject.Roles?.push(inputObject[`Roles[${index}]`]);
			index++;
		}

		const activeProfile = userObject.UserProfiles?.find(el => el.Id === userObject.CurrentProfileId);
		this.#appService.user = {userId: +userObject.Id!, userFirstName: userObject.FirstName!, userLastName: userObject.LastName! };
		this.#appService.companyId = +activeProfile?.Company?.Id!;
		this.#appService.userType = userObject.UserType!;
		
		return userObject;
	}

	getJwtToken() {
		const jwtPage = this.#contextService?.context?.Settings?.AppPaths?.Core?.Api + '/account/GetBasicStudiosJwtToken';
		return this.#httpClient.get(jwtPage, { withCredentials: true });
	}

	mapContext(pageInfo: any) {
		this.#contextService.context.Current.User = this.userMapper(pageInfo);
		this.#contextService.setObservableContext(this.#contextService.context);
	}

	parseUrl(url: string): UrlTree {
		return this.#router.parseUrl(url);
	}

	redirectToUnauthorized() {
		return this.parseUrl('error/unauthorized');
	}

	redirectToForbidden() {
		return this.parseUrl('error/forbidden');
	}

	redirectToHome() {
		return this.parseUrl('/');
	}

	redirectToNotFound() {
		return this.parseUrl('error/not-found');
	}
}
